import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import { FormBlock } from '@qbe/ads-blocks-forms';
import type { TextFieldProps } from '@qbe/ads-core';
import { Box, Icon, IconButton, Spacer, TextField, Typography } from '@qbe/ads-core';
import React, { useEffect, useState } from 'react';

import CustomScrollBar from '~/components-ds/CustomScrollBar';
import { useAppContent, useAppDispatch } from '~/hooks';
import { qalFormStartAction, qalTrackEventAction } from '~/thunks/analytics';
import { occupationSearchTypes } from '~/types/analytics';
import type { OccupationsListItem } from '~/types/refData';
import { analyticsConfig } from '~/utils/analytics/analyticsConfig';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { createOccupationSearcher } from './utils';
import { truncate, wordHighlighter } from './WordHighlighter';

type ListProps = {
  children: React.ReactNode | Array<React.ReactNode>;
};

export const List = ({ children }: ListProps) => <Box>{children}</Box>;

type ListItemProps = {
  children: React.ReactNode;
  onClick: () => unknown;
  itemCode: string;
};

export const ListItem = ({ children, onClick, itemCode }: ListItemProps) => (
  <Box
    data-testid={itemCode}
    tabIndex={0}
    sx={{
      marginBottom: '6px',
      '&:hover': {
        cursor: 'pointer',
      },
      '&:hover .listitem__content': {
        backgroundColor: '#F7F8F9',
      },
    }}
    onClick={onClick}
  >
    <Box className="listitem__content" padding="8px">
      {children}
    </Box>
  </Box>
);

type SearchOccupationListItemsFieldProps = {
  list: Array<OccupationsListItem>;
  selectItem: (item: OccupationsListItem) => void;
  onClear?: () => void;
  showAllBeforeFiltered?: boolean;
  isQuickSearch?: boolean;
};

const SearchOccupationsListItemsField = ({
  // full dataset to filter
  list = [],
  selectItem,
  onClear,
  showAllBeforeFiltered = false,
  isQuickSearch = false,
}: SearchOccupationListItemsFieldProps) => {
  const { getTextContent } = useAppContent({
    basePath: 'dsme.businessDetailsOccupancySearch.fields',
    tokens: { maxResults: () => String(MAX_RESULTS) },
  });

  const dispatch = useAppDispatch();

  const MIN_CHAR = showAllBeforeFiltered ? 1 : 3;
  const MAX_RESULTS = 10;

  const [value, setValue] = useState('');
  const [pattern, setPattern] = useState('');
  const [searchState, setSearchState] = useState('');

  // filtering function
  const filterList = list.length
    ? (searchTerm: string) => createOccupationSearcher(list)(searchTerm, { limit: MAX_RESULTS + 1 })
    : undefined;

  const handleClear = () => {
    setPattern('');
    setValue('');
    onClear && onClear();
  };

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  // Analytics - enhanced occupation search
  useEffect(() => {
    // User couldn't find occupation in app session.
    if (searchState === '') {
      dispatch(
        qalTrackEventAction({
          event: 'occupation_search',
          eventAction: 'quick search - occupation not found',
          eventLabel: 'occupation not found',
        })
      );
      // Use Search State to avoid double fire.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearchState('searched'); // TODO: Need to rewrite to comply with rules of hooks
      // Analytics - Future proof requirement for users select icon search due to quick search not found.
      analyticsConfig.occupationSearch = occupationSearchTypes.quickSearchNotFound;
    }
  }, [dispatch, searchState]);

  if (!filterList) {
    return null;
  }

  // filtered dataset to be used by the view
  const filteredList = filterList(pattern);

  const handleOnChange: TextFieldProps['onChange'] = (e) => {
    if (analyticsConfig.occupationSearch !== occupationSearchTypes.quickSearch)
      analyticsConfig.occupationSearch = occupationSearchTypes.quickSearch;
    handleFormStart();

    setValue(e.target.value);
    setPattern(e.target.value.length >= MIN_CHAR ? e.target.value : '');
  };

  const onSelectSearchItem = (item: OccupationsListItem) => {
    selectItem(item);
    if (item.desc) {
      setValue(item.desc);
    }
    setPattern('');
  };

  return (
    <Box sx={{ maxWidth: '400px', margin: '0 auto' }} data-testid="occupation-search">
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', left: '8px', top: '15px', zIndex: 1 }}>
          <Icon muiIcon={Search} size={28} />
        </Box>

        <TextField
          id="occupation-search"
          name="occupation-search"
          label={
            isQuickSearch
              ? getTextContent('quickSearchInput.placeholder')
              : getTextContent('iconSearchExactOccupationSearchModal.searchInputLabel')
          }
          autoComplete="off"
          fullWidth
          sx={{
            '& label': {
              marginLeft: '44px !important',
            },
            '& .MuiInputBase-root': {
              paddingLeft: '44px !important',
            },
          }}
          onChange={handleOnChange}
          data-track-value="mask"
          data-track-label="Type your specific occupation"
          value={value}
        />
        <Box sx={{ position: 'absolute', right: '8px', top: '17px', zIndex: 1 }}>
          <IconButton
            data-testid="occupation-search-clear"
            iconProps={{ muiIcon: Close, size: 24 }}
            onClick={handleClear}
          />
        </Box>
      </Box>
      <Spacer size="sm" />

      {showAllBeforeFiltered && !filteredList.length && (
        <CustomScrollBar maxHeight={350}>
          <Box paddingLeft="36px" paddingRight={0}>
            {list.map((item) => (
              <ListItem
                key={item.code}
                itemCode={item.code}
                onClick={() => onSelectSearchItem(item)}
              >
                <Typography variant="body1" fontWeight="fontWeightMedium" marginBottom="6px">
                  {truncate(item, {
                    property: 'desc',
                    textLength: 100,
                  })}
                </Typography>
                <Typography variant="body2" color="#99a2b2">
                  {truncate(item, {
                    property: 'anzsicShortDescription',
                    textLength: 100,
                  })}
                </Typography>
              </ListItem>
            ))}
          </Box>
        </CustomScrollBar>
      )}

      {filteredList.length ? (
        <>
          {pattern.length >= MIN_CHAR && (
            <CustomScrollBar maxHeight={350}>
              <Box paddingLeft="36px" paddingRight={0}>
                <List>
                  {filteredList.map((fuseResult) => {
                    const { item, refIndex } = fuseResult;
                    return (
                      <ListItem
                        key={refIndex}
                        itemCode={item.code}
                        onClick={() => onSelectSearchItem(item)}
                      >
                        <Typography
                          variant="body1"
                          fontWeight="fontWeightMedium"
                          marginBottom="6px"
                        >
                          {wordHighlighter(item, {
                            property: 'desc',
                            textLength: 100,
                            searchTerm: value,
                            color: 'rgb(0, 61, 165)', // TODO - token not available on theme
                          })}
                        </Typography>
                        <Typography variant="body2" color="#99a2b2">
                          {wordHighlighter(item, {
                            property: 'anzsicShortDescription',
                            searchTerm: value,
                            color: 'rgb(0, 61, 165)',
                            textLength: 100,
                          })}
                        </Typography>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </CustomScrollBar>
          )}
          {filteredList.length > MAX_RESULTS && (
            <Typography variant="body2" textAlign="center" marginTop={2}>
              {getTextContent('quickSearchInput.maxResultsHelperText')}
            </Typography>
          )}
        </>
      ) : (
        <FormBlock preset="text_subtitle">
          <Typography variant="body2" textAlign="center">
            {getTextContent('quickSearchInput.helperText')}
          </Typography>
        </FormBlock>
      )}
    </Box>
  );
};

export default SearchOccupationsListItemsField;
